'use client';

import type { FC, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const inputVariants = cva(
  'w-full bg-surface-input border border-border-subtle rounded-xl text-white font-[inherit] transition-[border-color] duration-200 focus:outline-none focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-subtle',
  {
    variants: {
      size: {
        s: 'px-3 py-2 text-sm',
        m: 'px-4 py-3 text-base',
        l: 'px-5 py-3.5 text-lg',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  },
);

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 's' | 'm' | 'l';
  multiline?: false;
};

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  size?: 's' | 'm' | 'l';
  multiline: true;
};

export const Input: FC<InputProps | TextareaProps> = ({ size = 'm', className, multiline, ...props }) => {
  const classes = cn(inputVariants({ size }), className);

  if (multiline) {
    return <textarea className={cn(classes, 'resize-none')} {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)} />;
  }

  return <input className={classes} {...(props as InputHTMLAttributes<HTMLInputElement>)} />;
};
